import { clamp } from 'asteroids-shared/helpers'

/**
 * Virtual playhead for snapshot interpolation: stays near `targetDelayMs` behind
 * the newest server time by gently adjusting playback rate so bursty network
 * delivery does not freeze or snap entities.
 */

const RATE_MIN = .85
const RATE_MAX = 1.10
// How far off-target (ms) before desired playback rate hits RATE_MIN / RATE_MAX.
const RATE_SATURATION_MS = 60
// Easing time constant (ms) for moving `rate` toward `desiredRate`.
const RATE_SMOOTH_MS = 120

export default class RenderClock {
  constructor({ targetDelayMs }) {
    this.targetDelayMs = targetDelayMs
    this.virtualTime = null
    this.rate = 1
  }

  initialized() {
    return this.virtualTime != null
  }

  reset() {
    this.virtualTime = null
    this.rate = 1
  }

  /**
   * Advance playhead by `deltaMs` (wall elapsed, or `simTicks * TICK_MS` to lock red to local sim ticks).
   * Returns updated virtual server time, or null if not ready.
   */
  advance(deltaMs, latestServerTime) {
    if (latestServerTime == null || !Number.isFinite(latestServerTime)) return null
    if (this.virtualTime != null && !Number.isFinite(this.virtualTime)) {
      this.virtualTime = null
      this.rate = 1
    }

    const wall = Number.isFinite(deltaMs) ? Math.max(0, deltaMs) : 0
    const delay = Number.isFinite(this.targetDelayMs) ? this.targetDelayMs : 0

    if (this.virtualTime == null) {
      const v = latestServerTime - delay
      if (!Number.isFinite(v)) return null
      this.virtualTime = v
      this.rate = 1
      return this.virtualTime
    }

    const target = latestServerTime - delay
    const err = target - this.virtualTime

    const sat = clamp(err / RATE_SATURATION_MS, -1, 1)
    const desiredRate = sat >= 0
      ? 1 + sat * (RATE_MAX - 1)
      : 1 + sat * (1 - RATE_MIN)

    const alpha = 1 - Math.exp(-wall / RATE_SMOOTH_MS)
    this.rate += (desiredRate - this.rate) * alpha

    this.virtualTime += wall * this.rate

    if (this.virtualTime > latestServerTime) {
      this.virtualTime = latestServerTime
    }

    if (!Number.isFinite(this.virtualTime)) {
      this.virtualTime = null
      this.rate = 1
      return null
    }

    return this.virtualTime
  }

  /**
   * Server time for SnapshotInterpolator.sample(): clamped between a floor
   * behind newest authority and `latest` (same upper bound as {@link advance}).
   */
  interpSampleTime(latestServerTime, renderDelayMs) {
    const vt = this.virtualTime
    const latest = Number.isFinite(latestServerTime) ? latestServerTime : null
    const delay = Number.isFinite(renderDelayMs) ? renderDelayMs : 0
    if (Number.isFinite(vt) && latest != null) {
      const minSample = latest - delay
      return clamp(vt, minSample, latest)
    }
    return Number.isFinite(vt) ? vt : latest != null ? latest - delay : 0
  }
}
